import type { FC, SVGProps } from "react";

type AvatarSvgProps = SVGProps<SVGSVGElement> & { size?: number };

const svgBase = (size: number): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: "0 0 48 48",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
});


const AceOfSpades: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a1a2e" />
    <path d="M24 8c-2 6-10 12-10 18a10 10 0 0 0 8.5 9.9C21 34 20 36 18 38h12c-2-2-3-4-4.5-2.1A10 10 0 0 0 34 26c0-6-8-12-10-18z" fill="#e8e8e8" />
    <circle cx="24" cy="26" r="3" fill="#1a1a2e" />
    <text x="8" y="16" fill="#e8e8e8" fontSize="10" fontWeight="bold" fontFamily="serif">A</text>
  </svg>
);

const RoyalFlush: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#8b0000" />
    {[0, 1, 2, 3, 4].map((i) => (
      <g key={i} transform={`translate(${4 + i * 8}, ${10 + (i % 2) * 3}) rotate(${-8 + i * 4})`}>
        <rect width="10" height="14" rx="1.5" fill="#fff" stroke="#8b0000" strokeWidth="0.5" />
        <text x="2" y="6" fill="#8b0000" fontSize="5" fontWeight="bold" fontFamily="serif">
          {["10", "J", "Q", "K", "A"][i]}
        </text>
      </g>
    ))}
    <path d="M24 32l2 4h-4z" fill="#ffd700" />
    <circle cx="24" cy="30" r="2" fill="#ffd700" />
  </svg>
);

const PokerChip: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#0d3b2e" />
    <circle cx="24" cy="24" r="16" fill="#c0392b" stroke="#ffd700" strokeWidth="2" />
    <circle cx="24" cy="24" r="11" fill="none" stroke="#fff" strokeWidth="1" strokeDasharray="3 2" />
    <circle cx="24" cy="24" r="7" fill="#c0392b" stroke="#ffd700" strokeWidth="1" />
    <text x="24" y="28" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">$</text>
    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
      <rect
        key={deg}
        x="22.5"
        y="7"
        width="3"
        height="4"
        rx="1"
        fill="#ffd700"
        transform={`rotate(${deg} 24 24)`}
      />
    ))}
  </svg>
);

const Dice: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a1a2e" />
    <g transform="translate(6, 10) rotate(-15, 18, 14)">
      <rect x="4" y="4" width="18" height="18" rx="3" fill="#fff" stroke="#333" strokeWidth="1" />
      <circle cx="9" cy="9" r="1.8" fill="#1a1a2e" />
      <circle cx="17" cy="9" r="1.8" fill="#1a1a2e" />
      <circle cx="13" cy="13" r="1.8" fill="#1a1a2e" />
      <circle cx="9" cy="17" r="1.8" fill="#1a1a2e" />
      <circle cx="17" cy="17" r="1.8" fill="#1a1a2e" />
    </g>
    <g transform="translate(16, 14) rotate(10, 14, 14)">
      <rect x="4" y="4" width="18" height="18" rx="3" fill="#c0392b" stroke="#8b0000" strokeWidth="1" />
      <circle cx="9" cy="9" r="1.8" fill="#fff" />
      <circle cx="17" cy="17" r="1.8" fill="#fff" />
    </g>
  </svg>
);

const Diamond: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a0a3e" />
    <path d="M24 6l14 16-14 20-14-20z" fill="none" stroke="#4fc3f7" strokeWidth="1.5" />
    <path d="M24 6l14 16-14 20-14-20z" fill="url(#dg)" />
    <path d="M10 22h28M24 6v36M14 14l10 28M34 14L24 42" stroke="#4fc3f7" strokeWidth="0.5" opacity="0.4" />
    <defs>
      <linearGradient id="dg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#4fc3f7" stopOpacity="0.6" />
        <stop offset="50%" stopColor="#e1f5fe" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#4fc3f7" stopOpacity="0.6" />
      </linearGradient>
    </defs>
  </svg>
);

const CardBack: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#0d1b2a" />
    <rect x="10" y="6" width="28" height="36" rx="3" fill="#1b3a5c" stroke="#ffd700" strokeWidth="1.5" />
    <rect x="13" y="9" width="22" height="30" rx="2" fill="none" stroke="#ffd700" strokeWidth="0.5" />
    <path d="M15 11l18 26M33 11L15 37" stroke="#ffd700" strokeWidth="0.5" opacity="0.5" />
    <path d="M24 18l3 2.5-1 4 3-2 3 2-1-4 3-2.5h-4L24 14l-2 4h-4l3 2.5-1 4 3-2 3 2" fill="#ffd700" />
  </svg>
);

const Trophy: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a1a2e" />
    <path d="M16 12h16v10c0 6-3.5 10-8 10s-8-4-8-10V12z" fill="#ffd700" />
    <path d="M16 14H10c0 5 3 8 6 8v-1" fill="none" stroke="#ffd700" strokeWidth="2" />
    <path d="M32 14h6c0 5-3 8-6 8v-1" fill="none" stroke="#ffd700" strokeWidth="2" />
    <rect x="20" y="32" width="8" height="4" rx="1" fill="#daa520" />
    <rect x="17" y="36" width="14" height="3" rx="1.5" fill="#daa520" />
    <path d="M24 16v7M20 20h8" stroke="#1a1a2e" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
  </svg>
);

const PokerTable: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#0d3b2e" />
    <ellipse cx="24" cy="24" rx="20" ry="13" fill="#1a5c3a" stroke="#daa520" strokeWidth="2" />
    <ellipse cx="24" cy="24" rx="14" ry="8" fill="none" stroke="#2a3a2e" strokeWidth="0.5" />
    <circle cx="18" cy="22" r="2.5" fill="#c0392b" stroke="#ffd700" strokeWidth="0.5" />
    <circle cx="24" cy="20" r="2.5" fill="#2980b9" stroke="#fff" strokeWidth="0.5" />
    <circle cx="30" cy="22" r="2.5" fill="#1a1a2e" stroke="#ffd700" strokeWidth="0.5" />
    <text x="24" y="30" textAnchor="middle" fill="#668060" fontSize="5" fontFamily="serif">DEALER</text>
  </svg>
);

const Joker: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#2d1b4e" />
    <circle cx="24" cy="20" r="9" fill="#e8c547" />
    <path d="M18 17c0-4 3-7 6-7s6 3 6 7" fill="#c0392b" />
    <path d="M15 17l3 0M30 17l3 0" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" />
    <circle cx="21" cy="19" r="1.5" fill="#1a1a2e" />
    <circle cx="27" cy="19" r="1.5" fill="#1a1a2e" />
    <path d="M20 24c2 2 6 2 8 0" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    <path d="M15 14l-2-4M24 10v-4M33 14l2-4" stroke="#ffd700" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="13" cy="9" r="2" fill="#c0392b" />
    <circle cx="24" cy="5" r="2" fill="#2ecc71" />
    <circle cx="35" cy="9" r="2" fill="#3498db" />
    <path d="M18 30l-3 10h18l-3-10" fill="#9b59b6" />
    <path d="M21 30v10M27 30v10" stroke="#7d3c98" strokeWidth="1" />
  </svg>
);

const AllIn: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a0000" />
    <text x="24" y="18" textAnchor="middle" fill="#c0392b" fontSize="8" fontWeight="bold" fontFamily="sans-serif">ALL</text>
    <text x="24" y="30" textAnchor="middle" fill="#ffd700" fontSize="14" fontWeight="900" fontFamily="sans-serif">IN</text>
    <path d="M8 34h32" stroke="#c0392b" strokeWidth="1" />
    <path d="M8 36h32" stroke="#ffd700" strokeWidth="0.5" />
    {[12, 20, 28, 36].map((x) => (
      <circle key={x} cx={x} cy="40" r="3" fill="#c0392b" stroke="#ffd700" strokeWidth="0.5" opacity="0.7" />
    ))}
  </svg>
);

const DeadMansHand: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a1a1a" />
    <g transform="translate(6, 6) rotate(-10, 18, 20)">
      <rect x="2" y="2" width="14" height="20" rx="2" fill="#222" stroke="#555" strokeWidth="0.8" />
      <text x="5" y="10" fill="#e8e8e8" fontSize="6" fontWeight="bold" fontFamily="serif">A</text>
      <path d="M6 14c-1 3-5 6-5 9a5 5 0 0 0 4 5c-1-1-1-2-2-1a5 5 0 0 0 7-5c0-3-3-6-4-8z" fill="#e8e8e8" transform="scale(0.6) translate(5,4)" />
    </g>
    <g transform="translate(18, 8) rotate(5, 14, 18)">
      <rect x="2" y="2" width="14" height="20" rx="2" fill="#222" stroke="#555" strokeWidth="0.8" />
      <text x="5" y="10" fill="#e8e8e8" fontSize="6" fontWeight="bold" fontFamily="serif">A</text>
      <text x="5" y="18" fill="#e8e8e8" fontSize="8" fontFamily="serif">&#9827;</text>
    </g>
    <g transform="translate(8, 20) rotate(-5, 12, 12)">
      <rect x="2" y="2" width="14" height="20" rx="2" fill="#222" stroke="#555" strokeWidth="0.8" />
      <text x="5" y="10" fill="#e8e8e8" fontSize="6" fontWeight="bold" fontFamily="serif">8</text>
      <path d="M6 14c-1 3-5 6-5 9a5 5 0 0 0 4 5c-1-1-1-2-2-1a5 5 0 0 0 7-5c0-3-3-6-4-8z" fill="#e8e8e8" transform="scale(0.6) translate(5,4)" />
    </g>
    <g transform="translate(22, 18) rotate(8, 12, 14)">
      <rect x="2" y="2" width="14" height="20" rx="2" fill="#222" stroke="#555" strokeWidth="0.8" />
      <text x="5" y="10" fill="#e8e8e8" fontSize="6" fontWeight="bold" fontFamily="serif">8</text>
      <text x="5" y="18" fill="#e8e8e8" fontSize="8" fontFamily="serif">&#9827;</text>
    </g>
    <path d="M10 44c4-2 8-6 14-6s10 4 14 6" stroke="#555" strokeWidth="0.5" fill="none" opacity="0.3" />
  </svg>
);

const Sunglasses: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#16213e" />
    <circle cx="24" cy="22" r="12" fill="#ffd5a0" />
    <path d="M12 20h24" stroke="#1a1a2e" strokeWidth="1.5" />
    <rect x="12" y="18" width="10" height="7" rx="2" fill="#1a1a2e" />
    <rect x="26" y="18" width="10" height="7" rx="2" fill="#1a1a2e" />
    <path d="M22 21h4" stroke="#1a1a2e" strokeWidth="1.5" />
    <path d="M13 22l8 2" stroke="#4fc3f7" strokeWidth="0.5" opacity="0.5" />
    <path d="M27 22l8 2" stroke="#4fc3f7" strokeWidth="0.5" opacity="0.5" />
    <path d="M20 28c2 1.5 6 1.5 8 0" stroke="#c0392b" strokeWidth="1" strokeLinecap="round" fill="none" />
    <path d="M18 12c3-4 9-4 12 0" fill="none" stroke="#333" strokeWidth="3" strokeLinecap="round" />
    <text x="24" y="44" textAnchor="middle" fill="#555568" fontSize="6">POKER FACE</text>
  </svg>
);


const Heisenberg: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#2c5f2d" />
    <circle cx="24" cy="22" r="11" fill="#f0d5a0" />
    <path d="M11 15l13-8 13 8" fill="#1a1a2e" />
    <rect x="11" y="14" width="26" height="4" rx="1" fill="#1a1a2e" />
    <rect x="14" y="18" width="20" height="2" fill="#333" />
    <rect x="14" y="19" width="9" height="6" rx="1" fill="#1a1a2e" stroke="#aaa" strokeWidth="0.5" />
    <rect x="25" y="19" width="9" height="6" rx="1" fill="#1a1a2e" stroke="#aaa" strokeWidth="0.5" />
    <path d="M23 22h2" stroke="#aaa" strokeWidth="0.8" />
    <path d="M18 29c3 2 9 2 12 0" stroke="#444" strokeWidth="1.5" fill="none" />
    <path d="M16 32c-2 4-4 8-4 10h24c0-2-2-6-4-10" fill="#1a1a2e" />
    <text x="24" y="46" textAnchor="middle" fill="#2ecc71" fontSize="5" fontWeight="bold" fontFamily="monospace">99.1%</text>
  </svg>
);

const BondJamesBond: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#0d0d0d" />
    <circle cx="24" cy="20" r="10" fill="#f0d5a0" />
    <path d="M14 14c2-5 8-6 10-5s8 0 10 5" fill="#1a1a2e" />
    <ellipse cx="20" cy="19" rx="2.5" ry="2" fill="#4a6fa5" />
    <ellipse cx="28" cy="19" rx="2.5" ry="2" fill="#4a6fa5" />
    <circle cx="20" cy="19" r="1" fill="#1a1a2e" />
    <circle cx="28" cy="19" r="1" fill="#1a1a2e" />
    <path d="M22 25c1 1 3 1 4 0" stroke="#c0392b" strokeWidth="0.8" fill="none" />
    <path d="M19 28l5 3 5-3" fill="#1a1a2e" />
    <rect x="22.5" y="28" width="3" height="16" fill="#fff" />
    <rect x="12" y="30" width="24" height="14" rx="0" fill="#1a1a2e" />
    <path d="M22.5 28l1.5 4 1.5-4" fill="#c0392b" />
    <rect x="22.5" y="32" width="3" height="12" fill="#fff" />
    <circle cx="38" cy="8" r="6" fill="none" stroke="#ffd700" strokeWidth="1" />
    <text x="38" y="11" textAnchor="middle" fill="#ffd700" fontSize="7" fontWeight="bold">7</text>
  </svg>
);

const Maverick: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#5c3a1e" />
    <circle cx="24" cy="20" r="10" fill="#f0d5a0" />
    <path d="M12 16c0-8 8-12 12-12s12 4 12 12" fill="#6d4c2a" />
    <path d="M10 16h28v3c0 1-2 2-14 2s-14-1-14-2v-3z" fill="#6d4c2a" />
    <path d="M10 16c-2 0-2 2 0 3h28c2 0 2-2 0-3" fill="#8b6914" />
    <circle cx="21" cy="19" r="1.5" fill="#1a1a2e" />
    <circle cx="27" cy="19" r="1.5" fill="#1a1a2e" />
    <path d="M21 25c1.5 1.5 4.5 1.5 6 0" stroke="#c0392b" strokeWidth="1" strokeLinecap="round" fill="none" />
    <rect x="16" y="30" width="16" height="14" rx="0" fill="#8b4513" />
    <path d="M22 30v14M26 30v14" stroke="#6d3a0a" strokeWidth="0.5" />
    <rect x="20" y="32" width="8" height="2" rx="0.5" fill="#333" />
    <g transform="translate(34, 30) rotate(15)">
      <rect x="0" y="0" width="8" height="12" rx="1" fill="#fff" stroke="#888" strokeWidth="0.5" />
      <text x="2" y="6" fill="#c0392b" fontSize="5" fontWeight="bold" fontFamily="serif">A</text>
      <text x="2" y="10" fill="#c0392b" fontSize="5" fontFamily="serif">&#9829;</text>
    </g>
  </svg>
);

const Rounders: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a1a2e" />
    <circle cx="24" cy="18" r="10" fill="#f0d5a0" />
    <path d="M14 14c2-4 5-6 10-6s8 2 10 6" fill="#3a2a1a" />
    <circle cx="21" cy="17" r="1.5" fill="#1a1a2e" />
    <circle cx="27" cy="17" r="1.5" fill="#1a1a2e" />
    <path d="M20 13h-3M28 13h3" stroke="#3a2a1a" strokeWidth="1" strokeLinecap="round" />
    <path d="M22 22c1 0.8 3 0.8 4 0" stroke="#888" strokeWidth="0.8" fill="none" />
    <rect x="14" y="28" width="20" height="16" rx="0" fill="#2c2c54" />
    <rect x="22" y="28" width="4" height="16" fill="#333" />
    <path d="M22 28l2 3 2-3" fill="#7f1d1d" />
    <g transform="translate(4, 32)">
      <circle cx="6" cy="6" r="5" fill="#c0392b" stroke="#ffd700" strokeWidth="1" />
      <circle cx="6" cy="6" r="3" fill="none" stroke="#ffd700" strokeWidth="0.3" strokeDasharray="1.5 1" />
      <text x="6" y="8" textAnchor="middle" fill="#fff" fontSize="5" fontWeight="bold">5K</text>
    </g>
    <g transform="translate(34, 34)">
      <circle cx="6" cy="6" r="5" fill="#1a1a2e" stroke="#ffd700" strokeWidth="1" />
      <text x="6" y="8" textAnchor="middle" fill="#ffd700" fontSize="5" fontWeight="bold">$</text>
    </g>
  </svg>
);

const TeddyKGB: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a0a0a" />
    <circle cx="24" cy="20" r="11" fill="#f0c8a0" />
    <path d="M13 18c1-6 5-10 11-10s10 4 11 10" fill="#2a1a0a" />
    <circle cx="20" cy="19" r="2" fill="#1a1a2e" />
    <circle cx="28" cy="19" r="2" fill="#1a1a2e" />
    <circle cx="20" cy="19" r="0.8" fill="#555" />
    <circle cx="28" cy="19" r="0.8" fill="#555" />
    <path d="M17 26c3 3 11 3 14 0" stroke="#333" strokeWidth="1.5" fill="none" />
    <path d="M15 28c-1 0-2 2-3 4h24c-1-2-2-4-3-4" fill="#333" />
    <rect x="14" y="32" width="20" height="12" fill="#7f1d1d" />
    <path d="M22 32v12M26 32v12" stroke="#5a1010" strokeWidth="0.5" />
    <g transform="translate(24, 40)">
      <rect x="-8" y="0" width="16" height="3" rx="1" fill="#c0392b" />
    </g>
    <circle cx="8" cy="8" r="5" fill="none" stroke="#c0392b" strokeWidth="1.5" />
    <path d="M5 5l6 6M11 5l-6 6" stroke="#c0392b" strokeWidth="0.5" opacity="0.5" />
    <path d="M5 11l1.5-1.5M11 11l-1.5-1.5M5 5l1.5 1.5M11 5l-1.5 1.5" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const OceansDanny: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#0a0a1e" />
    <circle cx="24" cy="20" r="10" fill="#d4a875" />
    <path d="M14 16c2-4 5-6 10-6s8 2 10 6" fill="#888" />
    <circle cx="21" cy="19" r="1.5" fill="#1a1a2e" />
    <circle cx="27" cy="19" r="1.5" fill="#1a1a2e" />
    <path d="M21 24c1.5 1 4.5 1 6 0" stroke="#333" strokeWidth="1" fill="none" />
    <rect x="14" y="28" width="20" height="16" fill="#1a1a2e" />
    <path d="M19 28l5 4 5-4" fill="#333" />
    <rect x="23" y="28" width="2" height="16" fill="#1a1a2e" />
    <path d="M23 28l1 3 1-3" fill="#a0a0a0" />
    <text x="24" y="46" textAnchor="middle" fill="#ffd700" fontSize="4" fontWeight="bold" letterSpacing="2">OCEAN</text>
  </svg>
);

const CasinoRoyalVillain: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a0a2e" />
    <circle cx="24" cy="20" r="10" fill="#e0c8a0" />
    <path d="M14 16c2-4 5-6 10-6s8 2 10 6" fill="#b0a090" />
    <circle cx="21" cy="18" r="2" fill="#fff" stroke="#aaa" strokeWidth="0.3" />
    <circle cx="21" cy="18" r="1" fill="#4a7" />
    <circle cx="27" cy="18" r="2" fill="#fff" stroke="#aaa" strokeWidth="0.3" />
    <circle cx="27" cy="18" r="1" fill="#b44" />
    <path d="M27 16.5l1.5-1" stroke="#b44" strokeWidth="0.3" />
    <path d="M21 24c1.5 0.5 4.5 0.5 6 0" stroke="#888" strokeWidth="0.8" fill="none" />
    <rect x="14" y="28" width="20" height="16" fill="#2c2c54" />
    <rect x="23" y="28" width="2" height="16" fill="#333" />
    <path d="M6 38l4-4 4 4 4-4 4 4" stroke="#c0392b" strokeWidth="1" fill="none" />
  </svg>
);

const TheCincinnatiKid: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#4a2810" />
    <circle cx="24" cy="20" r="10" fill="#f0d0a0" />
    <path d="M14 16c1-3 4-7 10-7s9 4 10 7" fill="#d4a030" />
    <circle cx="21" cy="19" r="1.5" fill="#3a6a9a" />
    <circle cx="27" cy="19" r="1.5" fill="#3a6a9a" />
    <path d="M22 24c1 1 3 1 4 0" stroke="#c0392b" strokeWidth="0.8" fill="none" />
    <rect x="14" y="28" width="20" height="16" fill="#c0392b" />
    <path d="M23 28l1 3 1-3" fill="#1a1a2e" />
    <rect x="23" y="31" width="2" height="13" fill="#fff" />
    <text x="24" y="46" textAnchor="middle" fill="#ffd700" fontSize="4" fontWeight="bold">THE KID</text>
  </svg>
);

const LadyLuck: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#2e1a3e" />
    <circle cx="24" cy="20" r="10" fill="#f5deb3" />
    <path d="M14 18c0-6 4-12 10-12s10 6 10 12c2 0 2 3 0 3-2 4-4 0-6 2-2-2-2 2-4 0s-2 2-4 0c-2-2-4 2-6-2-2 0-2-3 0-3z" fill="#c0392b" />
    <circle cx="21" cy="19" r="1.5" fill="#2ecc71" />
    <circle cx="27" cy="19" r="1.5" fill="#2ecc71" />
    <path d="M16 18l-2-1M32 18l2-1" stroke="#c0392b" strokeWidth="0.5" />
    <path d="M21 24c1.5 1 4.5 1 6 0" stroke="#c0392b" strokeWidth="1" fill="none" />
    <path d="M16 30c-2 4-2 10-2 14h20c0-4 0-10-2-14" fill="#9b59b6" />
    <path d="M22 30v14M26 30v14" stroke="#7d3c98" strokeWidth="0.5" />
    <path d="M20 34h8M20 38h8" stroke="#7d3c98" strokeWidth="0.3" />
    <path d="M24 6l1 2h2l-1.5 1.2.5 2-2-1.2-2 1.2.5-2L21 8h2z" fill="#ffd700" />
  </svg>
);

const WildWest: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#8b6914" />
    <circle cx="24" cy="22" r="10" fill="#f0d5a0" />
    <path d="M8 16c0-2 4-10 16-10s16 8 16 10c2 0 3 2 0 3H8c-3-1-2-3 0-3z" fill="#5a3810" />
    <path d="M12 16h24v2c0 1-4 2-12 2s-12-1-12-2v-2z" fill="#6d4c2a" />
    <circle cx="21" cy="21" r="1.5" fill="#1a1a2e" />
    <circle cx="27" cy="21" r="1.5" fill="#1a1a2e" />
    <path d="M17 26h14" stroke="#5a3810" strokeWidth="2" strokeLinecap="round" />
    <path d="M20 26v5M28 26v5M24 26v6" stroke="#5a3810" strokeWidth="1" />
    <path d="M14 32c0 4-2 8-2 12h24c0-4-2-8-2-12" fill="#8b6914" />
    <path d="M24 40l-4-4h8z" fill="#ffd700" />
  </svg>
);

const ScarfacePoker: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a0a0a" />
    <circle cx="24" cy="20" r="10" fill="#d4a875" />
    <path d="M14 16c2-5 5-7 10-7s8 2 10 7" fill="#1a1a2e" />
    <circle cx="21" cy="18" r="1.8" fill="#1a1a2e" />
    <circle cx="27" cy="18" r="1.8" fill="#1a1a2e" />
    <circle cx="21" cy="18" r="0.6" fill="#fff" />
    <circle cx="27" cy="18" r="0.6" fill="#fff" />
    <path d="M19 25c2.5 2 7.5 2 10 0" stroke="#333" strokeWidth="1" fill="none" />
    <path d="M22 24v3" stroke="#d4a875" strokeWidth="0.5" />
    <rect x="12" y="28" width="24" height="16" fill="#f5f5dc" />
    <rect x="22" y="28" width="4" height="8" fill="#f5f5dc" />
    <path d="M22 28l2 4 2-4" fill="#c0392b" />
    <path d="M12 28l2 2 2-2 2 2 2-2 2 2 2-2 2 2 2-2 2 2 2-2 2 2 2-2" stroke="#333" strokeWidth="0.3" fill="none" />
    <text x="24" y="46" textAnchor="middle" fill="#ffd700" fontSize="4" fontWeight="bold" fontFamily="serif">SAY HELLO</text>
  </svg>
);

const Cowboy: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#3a2510" />
    <circle cx="24" cy="22" r="10" fill="#f0d5a0" />
    <path d="M10 18c0-2 2-8 14-8s14 6 14 8l-4-2c-2-3-6-4-10-4s-8 1-10 4l-4 2z" fill="#8b4513" />
    <path d="M12 18h24c1 0 2 1 0 2-2 0-6 1-12 1s-10-1-12-1c-2-1-1-2 0-2z" fill="#6d3a0a" />
    <circle cx="21" cy="21" r="1.5" fill="#1a1a2e" />
    <circle cx="27" cy="21" r="1.5" fill="#1a1a2e" />
    <path d="M20 27c2 1.5 6 1.5 8 0" stroke="#8b4513" strokeWidth="1" fill="none" />
    <path d="M24 28v4" stroke="#333" strokeWidth="0.8" />
    <rect x="14" y="32" width="20" height="12" fill="#5a3510" />
    <path d="M24 35l-3-3h6z" fill="#1a1a2e" />
    <rect x="18" y="38" width="12" height="2" rx="1" fill="#ffd700" />
    <path d="M22 39l2 1 2-1" fill="#daa520" />
  </svg>
);

const HighRoller: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#0a0a2e" />
    <circle cx="24" cy="20" r="10" fill="#f0d5a0" />
    <path d="M14 16c2-4 5-6 10-6s8 2 10 6" fill="#1a1a2e" />
    <path d="M12 18l-2 1M36 18l2 1" stroke="#1a1a2e" strokeWidth="2" strokeLinecap="round" />
    <rect x="14" y="17" width="8" height="5" rx="2" fill="#141414" />
    <rect x="26" y="17" width="8" height="5" rx="2" fill="#141414" />
    <path d="M22 19.5h4" stroke="#141414" strokeWidth="1.5" />
    <path d="M15 18l6 3" stroke="#ffd700" strokeWidth="0.3" opacity="0.6" />
    <path d="M27 18l6 3" stroke="#ffd700" strokeWidth="0.3" opacity="0.6" />
    <path d="M22 26c1 0.5 3 0.5 4 0" stroke="#333" strokeWidth="0.8" fill="none" />
    <rect x="14" y="30" width="20" height="14" fill="#1a1a2e" />
    <rect x="23" y="30" width="2" height="14" fill="#222" />
    <path d="M23 30l1 3 1-3" fill="#ffd700" />
    <circle cx="24" cy="36" r="2" fill="#ffd700" />
    <text x="24" y="46" textAnchor="middle" fill="#ffd700" fontSize="4" fontWeight="bold">VIP</text>
  </svg>
);

const Skull: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a1a1a" />
    <path d="M14 22c0-8 4-14 10-14s10 6 10 14c0 4-2 6-4 7v5h-12v-5c-2-1-4-3-4-7z" fill="#e8e8d0" />
    <ellipse cx="19" cy="20" rx="3.5" ry="4" fill="#1a1a1a" />
    <ellipse cx="29" cy="20" rx="3.5" ry="4" fill="#1a1a1a" />
    <path d="M22 26l2 3 2-3" fill="#1a1a1a" />
    <path d="M18 32v4M22 32v4M26 32v4M30 32v4" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="19" cy="20" r="1.5" fill="#c0392b" opacity="0.6" />
    <circle cx="29" cy="20" r="1.5" fill="#c0392b" opacity="0.6" />
    <path d="M15 40c3-1 6 1 9-1s6 1 9-1" stroke="#555" strokeWidth="0.5" fill="none" />
  </svg>
);

const SharkFin: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#0a2e4a" />
    <path d="M6 30h36" stroke="#1a5a8a" strokeWidth="0.5" />
    <path d="M4 30c4 0 8 2 12 2s8-2 12-2 8 2 12 2 8-2 12-2" stroke="#1a5a8a" strokeWidth="1" fill="none" opacity="0.3" />
    <path d="M24 10l6 20h-12z" fill="#607d8b" />
    <path d="M24 10l3 10h-6z" fill="#78909c" />
    <path d="M6 32c8 0 12 4 18 4s10-2 18-2v10H6z" fill="#0d3a5a" opacity="0.6" />
    <circle cx="10" cy="38" r="1" fill="#1e1e30" />
    <circle cx="38" cy="40" r="1.5" fill="#1e1e30" />
    <circle cx="20" cy="42" r="0.8" fill="#1e1e30" />
  </svg>
);

const Fox: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a2e1a" />
    <path d="M10 18l6-12 4 10" fill="#d4590a" />
    <path d="M38 18l-6-12-4 10" fill="#d4590a" />
    <path d="M11 15l5-8 3 7" fill="#f0d0a0" />
    <path d="M37 15l-5-8-3 7" fill="#f0d0a0" />
    <ellipse cx="24" cy="24" rx="14" ry="12" fill="#d4590a" />
    <ellipse cx="24" cy="26" rx="8" ry="7" fill="#f0d0a0" />
    <ellipse cx="19" cy="21" rx="3" ry="2.5" fill="#fff" />
    <ellipse cx="29" cy="21" rx="3" ry="2.5" fill="#fff" />
    <circle cx="19" cy="21" r="1.5" fill="#2ecc71" />
    <circle cx="29" cy="21" r="1.5" fill="#2ecc71" />
    <circle cx="19" cy="21" r="0.5" fill="#1a1a2e" />
    <circle cx="29" cy="21" r="0.5" fill="#1a1a2e" />
    <ellipse cx="24" cy="27" rx="2" ry="1.5" fill="#1a1a2e" />
    <path d="M22 28.5l-3 2M26 28.5l3 2" stroke="#d4590a" strokeWidth="0.8" strokeLinecap="round" />
    <path d="M22 29l-4 1.5M22 29.5l-4 2M26 29l4 1.5M26 29.5l4 2" stroke="#d4590a" strokeWidth="0.3" />
  </svg>
);

const Wolf: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a1a2e" />
    <path d="M12 20l4-14 6 8" fill="#607d8b" />
    <path d="M36 20l-4-14-6 8" fill="#607d8b" />
    <path d="M13 17l3-10 4 6" fill="#f8bbd0" />
    <path d="M35 17l-3-10-4 6" fill="#f8bbd0" />
    <ellipse cx="24" cy="24" rx="14" ry="13" fill="#607d8b" />
    <ellipse cx="24" cy="27" rx="7" ry="6" fill="#90a4ae" />
    <ellipse cx="19" cy="20" rx="3.5" ry="3" fill="#fff" />
    <ellipse cx="29" cy="20" rx="3.5" ry="3" fill="#fff" />
    <circle cx="19" cy="20" r="2" fill="#ffd700" />
    <circle cx="29" cy="20" r="2" fill="#ffd700" />
    <circle cx="19" cy="20" r="1" fill="#1a1a2e" />
    <circle cx="29" cy="20" r="1" fill="#1a1a2e" />
    <ellipse cx="24" cy="27" rx="2.5" ry="2" fill="#1a1a2e" />
    <path d="M18 32l2-2h8l2 2" stroke="#fff" strokeWidth="0.8" fill="none" />
    <path d="M19 32l1.5 1.5h7l1.5-1.5" stroke="#fff" strokeWidth="0.8" fill="none" />
  </svg>
);

const Dragon: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a0a2e" />
    <path d="M10 14l6-6 2 8" fill="#4a0e4e" />
    <path d="M38 14l-6-6-2 8" fill="#4a0e4e" />
    <ellipse cx="24" cy="22" rx="14" ry="12" fill="#6a1b6a" />
    <path d="M10 22c-4-2-6 2-6 4s2 4 6 2" fill="#6a1b6a" />
    <path d="M38 22c4-2 6 2 6 4s-2 4-6 2" fill="#6a1b6a" />
    <ellipse cx="18" cy="20" rx="4" ry="3.5" fill="#ffd700" />
    <ellipse cx="30" cy="20" rx="4" ry="3.5" fill="#ffd700" />
    <ellipse cx="18" cy="20" rx="1.5" ry="3" fill="#1a1a2e" />
    <ellipse cx="30" cy="20" rx="1.5" ry="3" fill="#1a1a2e" />
    <path d="M20 28l4 4 4-4" fill="none" stroke="#ffd700" strokeWidth="1" />
    <ellipse cx="24" cy="28" rx="2" ry="1" fill="#1a1a2e" />
    <path d="M18 32l-2 6M24 34v6M30 32l2 6" stroke="#ff6b35" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
    <path d="M16 36l-4 8M24 38v6M32 36l4 8" stroke="#ff6b35" strokeWidth="0.8" strokeLinecap="round" opacity="0.3" />
  </svg>
);

const Eagle: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#0a1e3e" />
    <path d="M6 20c6-2 10-8 18-8s12 6 18 8c-2 4-8 6-18 6S8 24 6 20z" fill="#5a3a1a" />
    <ellipse cx="24" cy="22" rx="10" ry="9" fill="#fff" />
    <ellipse cx="19" cy="20" rx="3" ry="2.5" fill="#ffd700" />
    <ellipse cx="29" cy="20" rx="3" ry="2.5" fill="#ffd700" />
    <circle cx="19" cy="20" r="1.2" fill="#1a1a2e" />
    <circle cx="29" cy="20" r="1.2" fill="#1a1a2e" />
    <path d="M17 18l-3-2M31 18l3-2" stroke="#5a3a1a" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M22 26l2 4 2-4" fill="#ffa000" />
    <path d="M22 26l2 2 2-2" fill="#ffca28" />
    <path d="M6 20c-2 4 2 10 8 14M42 20c2 4-2 10-8 14" stroke="#5a3a1a" strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);

const Flame: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#1a0500" />
    <path d="M24 4c-4 8-14 16-14 26a14 14 0 0 0 28 0c0-10-10-18-14-26z" fill="#e65100" />
    <path d="M24 10c-3 6-10 12-10 20a10 10 0 0 0 20 0c0-8-7-14-10-20z" fill="#ff9100" />
    <path d="M24 18c-2 4-6 8-6 14a6 6 0 0 0 12 0c0-6-4-10-6-14z" fill="#ffd600" />
    <path d="M24 26c-1 2-3 4-3 7a3 3 0 0 0 6 0c0-3-2-5-3-7z" fill="#fff9c4" />
  </svg>
);

const Lightning: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#0a0a2e" />
    <path d="M28 4L16 24h8L18 44l18-24h-9z" fill="#ffd600" />
    <path d="M28 4L16 24h8L18 44l18-24h-9z" fill="url(#lg)" />
    <path d="M26 8l-8 14h6l-4 14 12-16h-7z" fill="#fff9c4" opacity="0.5" />
    <defs>
      <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fff" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#ff8f00" stopOpacity="0.3" />
      </linearGradient>
    </defs>
  </svg>
);

const FourLeafClover: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#0a2e0a" />
    <path d="M24 24c-2-4-8-10-8-14a6 6 0 0 1 12 0c0 4-2 10-4 14z" fill="#2ecc71" />
    <path d="M24 24c4-2 10-8 14-8a6 6 0 0 1 0 12c-4 0-10-2-14-4z" fill="#27ae60" />
    <path d="M24 24c2 4 8 10 8 14a6 6 0 0 1-12 0c0-4 2-10 4-14z" fill="#2ecc71" />
    <path d="M24 24c-4 2-10 8-14 8a6 6 0 0 1 0-12c4 0 10 2 14 4z" fill="#27ae60" />
    <circle cx="24" cy="24" r="3" fill="#1a5a1a" />
    <path d="M24 6v36M6 24h36" stroke="#1a5a1a" strokeWidth="0.5" opacity="0.3" />
  </svg>
);

const Mask: FC<AvatarSvgProps> = ({ size = 32, ...props }) => (
  <svg {...svgBase(size)} {...props}>
    <rect width="48" height="48" rx="8" fill="#2e1a3e" />
    <path d="M10 16c0-4 6-10 14-10s14 6 14 10v8c0 8-6 14-14 14S10 32 10 24v-8z" fill="#ffd700" />
    <path d="M10 16c0-4 6-10 14-10s14 6 14 10" fill="#e6b800" />
    <ellipse cx="18" cy="20" rx="4" ry="5" fill="#1a1a2e" />
    <ellipse cx="30" cy="20" rx="4" ry="5" fill="#1a1a2e" />
    <path d="M22 20c1-1 3-1 4 0" stroke="#ffd700" strokeWidth="1" fill="none" />
    <path d="M20 30c2 2 6 2 8 0" stroke="#e6b800" strokeWidth="1.5" fill="none" />
    <path d="M10 24l-4 0M38 24l4 0" stroke="#ffd700" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M18 8l-2-3M30 8l2-3M24 6v-3" stroke="#ffd700" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const POKER_AVATARS = [
  { id: "ace-of-spades", name: "Ace of Spades", component: AceOfSpades },
  { id: "royal-flush", name: "Royal Flush", component: RoyalFlush },
  { id: "poker-chip", name: "Poker Chip", component: PokerChip },
  { id: "dice", name: "Dice", component: Dice },
  { id: "diamond", name: "Diamond", component: Diamond },
  { id: "card-back", name: "Card Back", component: CardBack },
  { id: "trophy", name: "Trophy", component: Trophy },
  { id: "poker-table", name: "Poker Table", component: PokerTable },
  { id: "joker", name: "Joker", component: Joker },
  { id: "all-in", name: "All In", component: AllIn },
  { id: "dead-mans-hand", name: "Dead Man's Hand", component: DeadMansHand },
  { id: "sunglasses", name: "Poker Face", component: Sunglasses },
  { id: "heisenberg", name: "Heisenberg", component: Heisenberg },
  { id: "bond", name: "James Bond", component: BondJamesBond },
  { id: "maverick", name: "Maverick", component: Maverick },
  { id: "rounders", name: "Rounders", component: Rounders },
  { id: "teddy-kgb", name: "Teddy KGB", component: TeddyKGB },
  { id: "oceans", name: "Danny Ocean", component: OceansDanny },
  { id: "le-chiffre", name: "Le Chiffre", component: CasinoRoyalVillain },
  { id: "cincinnati-kid", name: "Cincinnati Kid", component: TheCincinnatiKid },
  { id: "lady-luck", name: "Lady Luck", component: LadyLuck },
  { id: "wild-west", name: "Wild West", component: WildWest },
  { id: "scarface", name: "Scarface", component: ScarfacePoker },
  { id: "cowboy", name: "Cowboy", component: Cowboy },
  { id: "high-roller", name: "High Roller", component: HighRoller },
  { id: "skull", name: "Skull", component: Skull },
  { id: "shark", name: "Shark", component: SharkFin },
  { id: "fox", name: "Fox", component: Fox },
  { id: "wolf", name: "Wolf", component: Wolf },
  { id: "dragon", name: "Dragon", component: Dragon },
  { id: "eagle", name: "Eagle", component: Eagle },
  { id: "flame", name: "On Fire", component: Flame },
  { id: "lightning", name: "Lightning", component: Lightning },
  { id: "clover", name: "Lucky Clover", component: FourLeafClover },
  { id: "mask", name: "Masquerade", component: Mask },
] as const;

export type PokerAvatarId = (typeof POKER_AVATARS)[number]["id"];

export function PokerAvatarIcon({ avatarId, size = 32 }: { avatarId: string; size?: number }) {
  const avatar = POKER_AVATARS.find((a) => a.id === avatarId);
  if (!avatar) return null;
  const Component = avatar.component;
  return <Component size={size} style={{ borderRadius: size * 0.16 }} />;
}
